import { useMemo } from "react";
import { GPUInstanceType } from "types/InstanceTypes";
import {
  IApplication,
  VerbBuildStatus,
  WorkspaceStatus,
} from "models/Workspace.model";
import { ContainerSelected, LifecycleScriptStatus } from "../shared/BuildTypes";
import { Environment } from "./types";
import {
  getEnvironmentStatus,
  getHealthChecksFromEnvironment,
  getIngressApplicationsFromEnvironment,
  getTunnelStatusFromEnvironment,
  getVerbBuildStatusFromTasks,
  getEnvironmentBillingState,
  normalizeEnvironmentStatus,
} from "./environtmentUtils";
import {
  isFreeTierInstance,
  getStoragePricing,
  useFreeTierForUserIds,
  withServiceCharge,
} from "utils/freeTierUtils";
import { getHealthCheckProbePort, HealthCheckLike } from "utils/healthChecks";
import { EnvironmentStatus } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";

export const getNotebookNameFromUrl = (url: string) => {
  const lastPart = url.split("/").pop() || "";
  const extractedName = lastPart.replace(".ipynb", "");
  return extractedName;
};

export const formatMemory = (
  memory: string | number,
  playformType: string,
): string | number => {
  if (playformType === "akash") {
    const index = (memory as string).indexOf("GiB");
    if (index !== -1) {
      return (memory as string).slice(0, index + 3);
    }
  }
  return memory;
};

export function useNotebookDetailsFromWorkspace(workspace, notebookList) {
  const notebookDetails = useMemo(() => {
    if (
      !workspace ||
      !workspace.fileObjects ||
      Object.entries(workspace.fileObjects).length === 0 ||
      !notebookList
    ) {
      return { name: "", path: "" };
    }
    const url =
      workspace.fileObjects[Object.keys(workspace.fileObjects)[0]].url;
    const currentName = getNotebookNameFromUrl(url);
    const found = notebookList.find((n) => {
      const extractedName = getNotebookNameFromUrl(n.file);
      return extractedName === currentName;
    });

    if (found) {
      return {
        name: currentName,
        path: found.name.replace(".html", ""),
        found: true,
      };
    }

    return { name: currentName, path: url, found: false };
  }, [workspace?.fileObjects, notebookList]);

  return notebookDetails;
}

export function isGPUfromInstanceType(
  instanceType: string,
  allInstancesAvailable: any[],
) {
  return !!allInstancesAvailable.find(
    (instance) => instance.type === instanceType,
  )?.supported_gpus;
}

interface TunnelApplication {
  cloudflareApplicationID: string;
  cloudflareDnsRecordID: string;
  healthCheckID: string;
  hostname: string;
  name: string;
}

interface HealthCheck extends HealthCheckLike {
  health_check_id: string;
  status: string;
}

export function getTunnelApplicationHealthCheck(
  healthChecks: HealthCheck[],
  application: TunnelApplication,
) {
  return (
    healthChecks.find(
      (healthCheck) =>
        healthCheck.health_check_id === application.healthCheckID,
    )?.status || "unknown"
  );
}

export function getStorageCostNumber(input: string): string {
  if (!input || typeof input !== "string") {
    return "0";
  }

  // Conversion factors to GiB
  const conversionFactors: Record<string, number> = {
    TiB: 1024,
    TB: 931.32, // 1 TB ≈ 931.32 GiB
    GiB: 1,
    GB: 0.9313, // 1 GB ≈ 0.9313 GiB
    MiB: 1 / 1024, // 1 MiB ≈ 0.0009766 GiB
    MB: 0.0009313, // 1 MB ≈ 0.0009313 GiB
    KiB: 1 / (1024 * 1024), // 1 KiB ≈ 9.537e-7 GiB
    KB: 9.313e-7, // 1 KB ≈ 9.313e-7 GiB
    B: 9.313e-10, // 1 B ≈ 9.313e-10 GiB
  };

  let totalGiB = 0;

  // Pattern to match number + unit combinations
  // This will match patterns like "23GiB", "1TiB", "128GiB", etc.
  const pattern = /(\d+(?:\.\d+)?)\s*([A-Za-z]+)/g;
  let match;

  while ((match = pattern.exec(input)) !== null) {
    const value = parseFloat(match[1]);
    const unit = match[2];

    // Find the conversion factor (case-insensitive)
    const conversionKey = Object.keys(conversionFactors).find(
      (key) => key.toLowerCase() === unit.toLowerCase(),
    );

    if (conversionKey && !isNaN(value)) {
      totalGiB += value * conversionFactors[conversionKey];
    }
  }

  // If no units were found, try to extract just the first number
  if (totalGiB === 0) {
    let result = "";
    for (let char of input) {
      if (isNaN(char as any) || char === " ") {
        break;
      }
      result += char;
    }
    totalGiB = parseFloat(result) || 0;
  }

  // Round to 2 decimal places and convert to string
  return totalGiB.toFixed(2).replace(/\.?0+$/, "");
}

export function getInstancePriceRate(
  runningCost: number,
  storageAmount: number,
  storageCost: number,
  isComputeBillable: boolean,
  isStorageBillable = true,
): string {
  const activeComputeCost = isComputeBillable ? runningCost : 0;
  const activeStorageCost = isStorageBillable ? storageAmount * storageCost : 0;
  return withServiceCharge(activeComputeCost + activeStorageCost).toFixed(2);
}

export interface BurnRateBreakdown {
  total: number;
  computeCost: number;
  storageCost: number;
}

export function calculateBurnRateBreakdown(
  environments: Environment[],
  isEntitled: (environment: Environment) => boolean = () => false,
): BurnRateBreakdown {
  const breakdown: BurnRateBreakdown = {
    total: 0,
    computeCost: 0,
    storageCost: 0,
  };

  for (const env of environments) {
    const instanceInfo = env.instance?.instance_type_info;
    if (!instanceInfo) continue;

    const baseCost = withServiceCharge(instanceInfo.base_price?.amount);
    const storageGiB = Number(
      getStorageCostNumber(env.instance?.disk_size || "") || 0,
    );
    const {
      totalCost: storageHourlyCost,
      withinFreeTier: isStorageWithinFreeTier,
    } = getStoragePricing(instanceInfo as GPUInstanceType, storageGiB);

    const billingState = getEnvironmentBillingState(env);
    const isRunning = billingState.compute;
    const isStorageBillable = billingState.storage;

    const environmentIsEntitled = isEntitled(env);
    const computeFree =
      environmentIsEntitled &&
      isFreeTierInstance(instanceInfo.base_price?.amount);
    const storageFree = environmentIsEntitled && isStorageWithinFreeTier;
    const envStorageCost =
      isStorageBillable && !storageFree ? storageHourlyCost : 0;
    const envComputeCost = isRunning && !computeFree ? baseCost : 0;

    breakdown.computeCost += parseFloat(envComputeCost.toFixed(2));
    breakdown.storageCost += parseFloat(envStorageCost.toFixed(2));
  }

  // Derive total from the rounded components so it always matches what the UI
  // shows when it displays computeCost and storageCost individually.
  breakdown.total =
    parseFloat(breakdown.computeCost.toFixed(2)) +
    parseFloat(breakdown.storageCost.toFixed(2));

  return breakdown;
}

export function useEnvironmentBurnRateBreakdown(
  environments: Environment[],
): BurnRateBreakdown {
  const creatorUserIds = useMemo(
    () => environments.map((environment) => environment.labels?.userId),
    [environments],
  );
  const { entitlementByUserId } = useFreeTierForUserIds(creatorUserIds);

  return useMemo(
    () =>
      calculateBurnRateBreakdown(environments, (environment) =>
        Boolean(
          environment.labels?.userId &&
          entitlementByUserId[environment.labels.userId],
        ),
      ),
    [entitlementByUserId, environments],
  );
}

export function parsePortMappingEntry(
  portsEncoded: string,
): Record<string, string> | null {
  if (!portsEncoded) {
    return null;
  }
  const portMappings: Record<string, string> = {};
  const pairs = portsEncoded.split("|"); // Split by encoded '|'

  pairs.forEach((pair) => {
    const [name, port] = pair.split(":"); // Split by encoded ':'
    portMappings[name] = port;
  });

  return portMappings;
}

export function displayPlatformType(platformType: string) {
  if (platformType === "GCPALPHA") {
    return "NVIDIA DGX Cloud";
  }
  if (
    platformType === "LAMBDA-LABS" ||
    platformType === "lambda-labs" ||
    platformType === "LAMBDALABS"
  ) {
    return "LAMBDA";
  }
  return platformType;
}

export function getCloudPlatformUrl(platformType: string) {
  if (platformType === "AMAYA") {
    return "https://ameyacloud.in/";
  } else if (platformType === "AWS") {
    return "https://aws.amazon.com";
  } else if (platformType === "AZURE") {
    return "https://azure.microsoft.com/";
  } else if (platformType === "BOOSTRUN") {
    return "https://boostrun.com/";
  } else if (platformType === "CRUSOE") {
    return "https://www.crusoe.ai";
  } else if (platformType === "CUDO") {
    return "https://www.cudocompute.com/";
  } else if (platformType === "DATACRUNCH") {
    return "https://datacrunch.io/";
  } else if (platformType === "DENVR") {
    return "https://www.denvrdata.com/";
  } else if (platformType === "DIGITALOCEAN") {
    return "https://www.digitalocean.com/";
  } else if (platformType === "EVERGREEN") {
    return "https://evergreen.insightglobal.com/technology-services/cloud-services/";
  } else if (platformType === "FLUIDSTACK") {
    return "https://fluidstack.com/";
  } else if (platformType === "GCP") {
    return "https://cloud.google.com/";
  } else if (platformType === "HYDRA") {
    return "https://hydrahost.com/";
  } else if (platformType === "HYPERSTACK") {
    return "https://www.hyperstack.cloud/";
  } else if (platformType === "LAMBDA-LABS" || platformType === "LAMBDALABS") {
    return "https://lambda.ai/";
  } else if (platformType === "LATITUDE") {
    return "https://www.latitude.sh/";
  } else if (platformType === "MASSEDCOMPUTE") {
    return "https://massedcompute.com/";
  } else if (platformType === "NEBIUS") {
    return "https://nebius.com/";
  } else if (platformType === "OCI") {
    return "https://www.oracle.com/cloud/";
  } else if (platformType === "PAPERSPACE") {
    return "https://www.paperspace.com/";
  } else if (platformType === "SCALEWAY") {
    return "https://www.scaleway.com/en/";
  } else if (platformType === "SHADEFORM") {
    return "https://www.shadeform.ai/";
  } else if (platformType === "VULTR") {
    return "https://www.vultr.com/";
  } else if (platformType === "GCPALPHA") {
    return "https://www.nvidia.com/en-us/data-center/dgx-cloud/";
  }

  return "https://www.shadeform.ai";
}

export const dummyDGXInstanceObject: GPUInstanceType = {
  type: "a3-megagpu-8g:nvidia-h100-mega-80gb:8",
  quota: {
    on_demand: {},
    spot: {},
    reserved: {
      id: "",
      name: "",
      maximum: 0,
      current: 0,
      unit: "",
    },
  },
  supported_gpus: [
    {
      count: 8,
      memory: "80GiB",
      manufacturer: "NVIDIA",
      name: "H100",
    },
  ],
  memory: "1TiB848GiB",
  vcpu: 208,
  supported_architectures: ["x86_64"],
  supported_num_cores: [],
  clock_speed_in_ghz: "0.000000",
  sub_location: "europe-west4-b",
  elastic_root_volume: true,
  location: "europe-west4",
  is_available: true,
  network_performance: "",
  available_azs: [],
  default_cores: 0,
  maximum_network_interfaces: 0,
  prices: [],
  base_price: {
    currency: "USD",
    amount: "39.324000",
  },
  default_price: "",
  supported_storage: [],
  sub_location_type_changeable: true,
  cloud_creds: [
    {
      id: "dgxc-gcp",
      baseDns: "",
      createdAt: "",
      name: "dgxc-gcp",
      platform: "dev-plane",
      platformId: "dgxc-gcp-1",
      platformRegion: "us-east-1",
      platformType: "gcpalpha",
      status: "RUNNING",
      tenantType: "isolated",
      version: "v0.1.0",
      locations: [
        {
          name: "europe-west4",
          description: "europe-west4",
          available: true,
          country: "NLD",
        },
      ],
    },
  ],
};

export function getJupyterHealthcheck(
  healthCheck: HealthCheck[],
  jupyterApp?: { healthCheckID: string },
): HealthCheck | undefined {
  if (!healthCheck || !jupyterApp) {
    return undefined;
  }

  return healthCheck.find((hc: HealthCheck) => {
    return hc.health_check_id === jupyterApp.healthCheckID;
  });
}

const isHealthyFromTunnelStatus = (
  healthCheck: HealthCheck[],
  tunnelStatus: string,
  environmentIsRunning: boolean,
  containerStatus: VerbBuildStatus,
  jupyterApp: any,
  isSkybridge: boolean,
  hasBackendStatus: boolean,
): boolean => {
  if (isSkybridge) {
    return (
      environmentIsRunning &&
      (hasBackendStatus || containerStatus === VerbBuildStatus.Completed)
    );
  }

  const basicInstanceHealthyStatus = !!(
    environmentIsRunning &&
    healthCheck &&
    healthCheck.length > 0 &&
    (hasBackendStatus || containerStatus === VerbBuildStatus.Completed)
  );
  // Only the jupyter app's individual health check determines notebook availability.
  // The aggregate tunnelStatus reflects all services — other services being unhealthy
  // should not block access to the notebook.
  const jupyterHealthyStatus = !!(
    jupyterApp != undefined &&
    getJupyterHealthcheck(healthCheck, jupyterApp)?.status === "healthy"
  );
  const tunnelHealthyStatus = tunnelStatus === "HEALTHY";
  return (
    basicInstanceHealthyStatus && jupyterHealthyStatus && tunnelHealthyStatus
  );
};

export const isHealthy = (
  environment: Environment,
  jupyterApp: any,
): boolean => {
  const healthChecks = getHealthChecksFromEnvironment(environment);
  const backendEnvironmentStatus = normalizeEnvironmentStatus(
    environment.status,
  );
  const hasBackendStatus = backendEnvironmentStatus !== null;
  const environmentIsRunning = hasBackendStatus
    ? backendEnvironmentStatus === EnvironmentStatus.RUNNING
    : getEnvironmentStatus(environment) === WorkspaceStatus.Running;
  return isHealthyFromTunnelStatus(
    healthChecks,
    getTunnelStatusFromEnvironment(environment),
    environmentIsRunning,
    getVerbBuildStatusFromTasks(environment.tasks || []),
    jupyterApp,
    environment.labels?.["tunnel-provider"] === "skybridge",
    hasBackendStatus,
  );
};

/**
 * Finds the Jupyter notebook tunnel app for a workspace.
 * This is the single source of truth for identifying which tunnel application
 * corresponds to the "Open Notebook" button.
 */
export function findNotebookApp(
  applications: IApplication[],
  workspaceId: string | undefined,
): IApplication | undefined {
  if (!workspaceId) return undefined;
  return applications.find(
    (app) =>
      app.name.startsWith("jupyter") && app.name.endsWith("-" + workspaceId),
  );
}

/**
 * Returns true if the deployment is expected to expose a Jupyter notebook.
 * Used to decide whether to seed the health check step and show the "Open
 * Notebook" button before tunnel applications arrive.
 *
 * - dockerCompose: jupyter only when jupyterInstall is true OR a port named
 *   "jupyter" is configured (the compose image ships its own Jupyter).
 * - vmBuild: jupyter only when forceJupyterInstall is true.
 * - verbBuild / customContainer: jupyter is always included.
 */
export function isJupyterDeployment(
  containerSelected: ContainerSelected | undefined,
  portMappings: Record<string, string> | null | undefined,
): boolean {
  const hasJupyterPortName =
    portMappings != null &&
    Object.keys(portMappings).some((name) =>
      name.toLowerCase().startsWith("jupyter"),
    );
  if (containerSelected?.dockerCompose) {
    return containerSelected.dockerCompose.jupyterInstall || hasJupyterPortName;
  }
  if (containerSelected?.vmBuild) {
    return !!containerSelected.vmBuild.forceJupyterInstall;
  }
  return true;
}

/**
 * Returns true if the environment has a CTA configured via IngressGateway.
 * Checks both the build spec (available in the initial create response) and the
 * runtime ingress_gateways list (available after provisioning).
 */
export function hasCTAIngress(environment: Environment | undefined): boolean {
  if (
    (environment?.ingress_gateways ?? []).some((gw) =>
      (gw.ingresses ?? []).some((i) => i.labels?.["cta-enabled"] === "true"),
    )
  )
    return true;
  return (environment?.build?.ingress_gateway ?? []).some((gwReq) =>
    (gwReq.ingresses ?? []).some((i) => i.labels?.["cta-enabled"] === "true"),
  );
}

const getDeployStatusTargetApplication = (
  environment: Environment,
): IApplication | undefined => {
  const applications = getIngressApplicationsFromEnvironment(environment);
  return (
    applications.find((app) => app.labels?.["cta-enabled"] === "true") ??
    applications[0]
  );
};

const getApplicationPort = (
  application: IApplication | undefined,
): string | undefined =>
  application?.port == null ? undefined : String(application.port);

const isApplicationHealthCheck = (
  application: IApplication,
  healthCheck: HealthCheck,
): boolean => {
  if (
    application.healthCheckID &&
    healthCheck.health_check_id === application.healthCheckID
  ) {
    return true;
  }

  const applicationPort = getApplicationPort(application);
  return (
    !!applicationPort &&
    getHealthCheckProbePort(healthCheck) === applicationPort
  );
};

const getApplicationHealthCheck = (
  environment: Environment,
  application: IApplication | undefined,
): HealthCheck | undefined => {
  if (!application) return undefined;
  return getHealthChecksFromEnvironment(environment).find((healthCheck) =>
    isApplicationHealthCheck(application, healthCheck),
  );
};

const getApplicationHealthStatus = (
  environment: Environment,
  application: IApplication | undefined,
): string | null => {
  return getApplicationHealthCheck(environment, application)?.status ?? null;
};

/**
 * Returns the deploying panel's target service health status for the timeout
 * timer. Returns "healthy", "unhealthy", "unknown", or null when no ingress
 * service is present.
 */
export function getCtaServiceStatus(
  environment: Environment | undefined,
): string | null {
  if (!environment) return null;
  const targetApp = getDeployStatusTargetApplication(environment);
  if (!targetApp) return null;
  return getApplicationHealthStatus(environment, targetApp) ?? "unknown";
}

/**
 * Returns true when the service currently blocking launchable readiness has
 * reported unhealthy. CTA/ingress status wins; notebook status is only a
 * fallback while legacy tunnel applications are present without an ingress app.
 */
export function isDeployingServiceUnhealthy(
  environment: Environment | undefined,
  notebookApp: IApplication | undefined,
): boolean {
  if (!environment) return false;

  const applications = getIngressApplicationsFromEnvironment(environment);
  const hasCta = applications.some(
    (app) => app.labels?.["cta-enabled"] === "true",
  );
  const ctaStatus = getCtaServiceStatus(environment);
  if (ctaStatus === "unhealthy") return true;

  return (
    !hasCta &&
    ctaStatus === null &&
    !!notebookApp &&
    getHealthChecksFromEnvironment(environment).find(
      (hc) => hc.health_check_id === notebookApp.healthCheckID,
    )?.status === "unhealthy"
  );
}

/**
 * Returns true when the deploying panel's target service is healthy. The
 * target is the first CTA service when present; otherwise it is the first
 * exposed ingress service. This intentionally avoids aggregate gateway status
 * because that reflects every exposed service.
 */
export function isCtaServiceReady(
  environment: Environment | undefined,
): boolean {
  if (!environment) return false;
  return getCtaServiceStatus(environment) === "healthy";
}

export const isLifecycleScriptTerminal = (
  status?: LifecycleScriptStatus,
): boolean =>
  status === LifecycleScriptStatus.COMPLETED ||
  status === LifecycleScriptStatus.FAILED;

export function isStartupScriptPending(
  environment: Environment | null | undefined,
): boolean {
  const status = environment?.lifecycle_scripts?.[0]?.last_run?.status;
  return (
    status === LifecycleScriptStatus.PENDING ||
    status === LifecycleScriptStatus.RUNNING
  );
}

export function isStartupScriptFailed(
  environment: Environment | null | undefined,
): boolean {
  return (
    environment?.lifecycle_scripts?.[0]?.last_run?.status ===
    LifecycleScriptStatus.FAILED
  );
}
